import React, { ReactNode } from 'react'
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary'
import { useAccountMeQuery } from '~/api/accounts'
import { FetchBaseError } from '~/common/fetchErrors'
import { Button, ButtonGroup, ButtonProps, Code, EmptyState, Flex, Text } from '~/design_system'
import { Msg, useMsg } from '~/common/localizationUtils'
import { links } from '~/common/links'
import { trackError } from '~/third_party/analytics'

/**
 * Wrap an entire page in an ErrorBoundary that shows a full-page error.
 *
 * Note: Next.js pages already have this built-in, so only use this for non-next pages.
 *
 * @example
 * <PageErrorBoundary>
 *   <MyPage />
 * </PageErrorBoundary>
 */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ReactErrorBoundary
      onError={(error, info) => {
        // Determine error subtype for better categorization in Mixpanel
        let errorSubtype = 'unknown'
        if (error instanceof FetchBaseError) {
          errorSubtype = `http_${error.status}`
        }

        // Track the error to Mixpanel
        trackError(error, {
          error_type: 'react_boundary_error',
          error_subtype: errorSubtype,
          component_stack: info.componentStack?.substring(0, 1000) // Truncate component stack
        })
      }}
      fallbackRender={props => {
        if (props.error instanceof FetchBaseError) {
          if (props.error.status === 401) return <NotLoggedInPage />
          if (props.error.status === 403) return <AccessDeniedPage />
          if (props.error.status === 404) return <NotFoundPage />
        }
        return <UnexpectedErrorPage {...props} />
      }}>
      {children}
    </ReactErrorBoundary>
  )
}

/**
 * "Go to homepage" button that sends candidates to their timesheets landing page.
 * Candidates can't access /home/all, and the top nav logo already routes them to
 * timesheets the same way (VNDLY-66271).
 */
export function GoToHomepageButton(props: Omit<ButtonProps, 'href' | 'children'>) {
  // Error pages must never kick the user to login if the account lookup fails.
  const { isCandidateRole, isLoading } = useAccountMeQuery(undefined, { disableRedirects: true })
  const href = !isLoading && isCandidateRole ? links.candidateTimesheets.href : links.home.href
  return (
    <Button variant="primary" href={href} {...props}>
      <Msg id="common.generic.error.go_to_the_homepage_btn" />
    </Button>
  )
}

/**
 * This component is using the new Empty State from the design system, however we would like to eventually
 * remove these components in favor of using the Empty State from the design system directly.
 * */
export function NotFoundPage() {
  const msg = useMsg()
  return (
    <EmptyState>
      <EmptyState.NoResults title={msg('common.generic.error.page_not_found')} mb={2} />
      <GoToHomepageButton mt="1rem !important" />
    </EmptyState>
  )
}

/**
 * This component is using the new Empty State from the design system, however we would like to eventually
 * remove these components in favor of using the Empty State from the design system directly.
 * */
export function AccessDeniedPage() {
  return (
    <EmptyState>
      <EmptyState.LockedOut />
      <EmptyState.Text maxW="75ch" mb={2}>
        <Msg id="common.generic.error.check_your_credentials" />
      </EmptyState.Text>
      <GoToHomepageButton />
    </EmptyState>
  )
}

/**
 * This component is using the new Empty State from the design system, however we would like to eventually
 * remove these components in favor of using the Empty State from the design system directly.
 * */
export function NotLoggedInPage() {
  const msg = useMsg()
  return (
    <EmptyState>
      <EmptyState.LockedOut title={msg('common.generic.error.not_logged_in')} />
      <EmptyState.Text maxW="75ch" mb={2}>
        <Msg id="common.generic.error.redirecting_you_to_sign_in" />
      </EmptyState.Text>
      <Button variant="primary" href={links.homeLogin.href}>
        <Msg id="common.ui.sign_in" />
      </Button>
    </EmptyState>
  )
}

/**
 * This component is using the new Empty State from the design system, however we would like to eventually
 * remove these components in favor of using the Empty State from the design system directly.
 * */
export function UnexpectedErrorPage(props: { trace_id?: string; description?: string } & Partial<FallbackProps>) {
  return (
    <EmptyState>
      <EmptyState.UnexpectedError />
      {props.error?.message && process.env.NODE_ENV === 'development' && (
        <Flex gap={1}>
          <Text textStyle="bodySemi">Error:</Text>
          <Code>{props.error.message}</Code>
        </Flex>
      )}
      <EmptyState.Text mb={2}>
        {props.description || <Msg id="common.generic.error.not_sure_what_went_wrong" />}
      </EmptyState.Text>
      {props.trace_id && (
        <Text textStyle="subtextSmall" mb={2}>
          <Msg id="common.ui.support_code" />: {props.trace_id}
        </Text>
      )}
      <ButtonGroup>
        <GoToHomepageButton />
      </ButtonGroup>
    </EmptyState>
  )
}
